import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
// App shell fork: adds /reset-password and LoginFormV2 (forgot password). Keep in sync with App.js when that file changes.
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuthContext } from './context/AuthContext';
import { useWebSocket } from './hooks/useWebSocket';
import { useJobs } from './hooks/useJobs';
import { useLocations } from './hooks/useLocations';
import { useToast } from './hooks/use-toast';
import { usePageSeo } from './hooks/usePageSeo';
import LoadingSpinner from './components/common/LoadingSpinner';
import AnalyticsBootstrap from './components/common/AnalyticsBootstrap';
import Website from './components/website/Website';
import PrivacyPolicyPage from './components/website/PrivacyPolicyPage';
import TermsOfServicePage from './components/website/TermsOfServicePage';
import apiService from './services/api';

const LoginForm = lazy(() => import('./components/auth/LoginFormV2'));
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage'));
const DashboardShell = lazy(() => import('./components/layout/DashboardShell'));
const AppStoreQRPopup = lazy(() => import('./components/common/AppStoreQRPopup'));

const HomePage = lazy(() => import('./components/website/HomePage'));
const AboutPage = lazy(() => import('./components/website/AboutPage'));
const PricingPage = lazy(() => import('./components/website/PricingPage'));
const ContactPage = lazy(() => import('./components/website/ContactPage'));
const ReviewsPage = lazy(() => import('./components/website/ReviewsPage'));
const WebScrapingPage = lazy(() => import('./components/website/WebScrapingPage'));
const WebIntegrationsPage = lazy(() => import('./components/website/WebIntegrationsPage'));
const ScraperLandingPage = lazy(() => import('./components/website/ScraperLandingPage'));
const NotFoundPage = lazy(() => import('./components/website/NotFoundPage'));
const MARKETING_QR_PATHS = new Set([
  '/',
  '/about',
  '/pricing',
  '/reviews',
  '/contact',
  '/web-scraping',
  '/web-integrations',
  '/privacy',
  '/terms',
]);

function isMarketingQrPath(pathname) {
  return MARKETING_QR_PATHS.has(pathname) || pathname.startsWith('/scrapers/');
}

const ReferralCodeHandler = () => {
  const location = useLocation();
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
    }
  }, [location]);
  return null;
};

function PublicAppStoreQRPopup() {
  const location = useLocation();
  if (!isMarketingQrPath(location.pathname)) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <AppStoreQRPopup />
    </Suspense>
  );
}

function SessionExpiredBridge() {
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const { toast } = useToast();
  const lastFireRef = useRef(0);
  useEffect(() => {
    const onSessionExpired = (e) => {
      const now = Date.now();
      if (now - lastFireRef.current < 2500) return;
      lastFireRef.current = now;
      logout();
      const msg =
        e.detail?.message || 'Your session has expired. Please sign in again.';
      toast({
        title: 'Session ended',
        description: msg,
        variant: 'destructive',
      });
      navigate('/login', { replace: true });
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, [logout, navigate, toast]);
  return null;
}

function LoginRoute() {
  const { user, loading: authLoading, login } = useAuthContext();
  usePageSeo('login');

  const handleLogin = async (loginData) => {
    const result = await login(loginData);
    if (!result.success) {
      throw new Error(result.error || 'Login failed');
    }
  };

  if (authLoading) return <LoadingSpinner />;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <LoginForm onLogin={handleLogin} />
    </Suspense>
  );
}

function ResetPasswordRoute() {
  const { user, loading: authLoading } = useAuthContext();
  usePageSeo({
    title: 'Reset Password | RINK Data',
    description: 'Reset your RINK Data account password.',
    path: '/reset-password',
    robots: 'noindex, nofollow',
  });

  if (authLoading) return <LoadingSpinner />;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ResetPasswordPage />
    </Suspense>
  );
}

function PrivacyRoute() {
  usePageSeo('privacy');
  return <PrivacyPolicyPage />;
}

function TermsRoute() {
  usePageSeo('terms');
  return <TermsOfServicePage />;
}

/** Authenticated app shell — heavy hooks only mount when user hits protected routes. */
function ProtectedApp() {
  const { user, loading: authLoading, logout, changePassword, cancelSubscription } = useAuthContext();
  const {
    jobs,
    setJobs,
    fetchJobs,
    dashboardStats,
    fetchDashboardStats,
    pagination,
    loading: jobsLoading,
    searchTerm,
    setSearchTerm,
    deleteJob,
    getJobResults,
    downloadResults,
    confirmDialog,
    handleConfirmDelete,
    closeConfirmDialog,
    showDeleteConfirmation,
  } = useJobs();
  const { locations, fetchStates } = useLocations();
  const { toast } = useToast();
  const [scrapers, setScrapers] = useState([]);
  const [activeView, setActiveView] = useState('dashboard');
  const [activeScraper, setActiveScraper] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [resultsDialog, setResultsDialog] = useState({ open: false, job: null, results: [] });
  const [profileDialog, setProfileDialog] = useState({ open: false });
  const [passwordDialog, setPasswordDialog] = useState({ open: false });
  const [subscriptionDialog, setSubscriptionDialog] = useState({ open: false });
  const [paymentMethodDialog, setPaymentMethodDialog] = useState({ open: false });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [shownToasts, setShownToasts] = useState(new Set());
  const modalOpenRef = useRef(false);

  const handleWebSocketMessage = useCallback(
    (data) => {
      if (activeView !== 'dashboard') return;

      if (
        data.type === 'job_created' ||
        data.type === 'job_status_update' ||
        data.type === 'job_updated' ||
        data.type === 'job_completed'
      ) {
        if (data.type === 'job_created') {
          if (!modalOpenRef.current) fetchJobs();
        } else {
          setJobs((prevJobs) =>
            prevJobs.map((job) =>
              job.id === data.job_id
                ? {
                    ...job,
                    status: data.status,
                    ...(data.results_count !== undefined && { results_count: data.results_count }),
                    ...(data.message && { message: data.message }),
                    ...(data.completed_at && { completed_at: data.completed_at }),
                    ...(data.updated_at && { updated_at: data.updated_at }),
                  }
                : job
            )
          );
        }
        if (data.type === 'job_status_update' || data.type === 'job_updated') {
          if (data.status === 2 || data.status === 3) {
            const toastKey = `${data.job_id}_${data.status}`;
            if (!shownToasts.has(toastKey)) {
              const statusMessages = {
                2: 'Job completed successfully',
                3: 'Job failed',
              };
              const message = data.message || statusMessages[data.status] || 'Job status updated';
              const scraperType = data.scraper_type || 'scraping';
              toast({
                title: `${scraperType} Job Complete`,
                description: `${message}`,
                variant: data.status === 2 ? 'default' : 'destructive',
              });
              setShownToasts((prev) => new Set([...prev, toastKey]));
            }
          }
        }
      }
    },
    [modalOpenRef, setJobs, fetchJobs, toast, shownToasts, activeView]
  );
  useWebSocket(handleWebSocketMessage);

  useEffect(() => {
    if (user) {
      fetchScrapers();
      fetchJobs(1, '');
      fetchDashboardStats();
      fetchStates();
    }
  }, [user]);

  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setShownToasts(new Set());
    }, 300000);
    return () => clearInterval(cleanupInterval);
  }, []);

  useEffect(() => {
    modalOpenRef.current = false;
  }, [activeView]);

  const handlePageChange = (page) => {
    fetchJobs(page, searchTerm);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    fetchJobs(1, term);
  };

  const handleLogout = () => {
    logout();
    setShownToasts(new Set());
  };

  const fetchScrapers = async () => {
    try {
      const data = await apiService.getScrapers();
      setScrapers(data);
    } catch (error) {
      // ignore
    }
  };

  const submitRinkJob = async (jobData) => {
    try {
      await apiService.createRinkJob(jobData);
      toast({
        title: 'Rink Job Submitted',
        description: 'Your scraping job has been submitted and is now processing.',
        variant: 'default',
      });
      fetchJobs();
      setActiveView('dashboard');
    } catch (error) {
      toast({
        title: 'Job Submission Failed',
        description: error.message || 'Failed to submit Rink scraping job',
        variant: 'destructive',
      });
    }
  };

  const submitFacebookJob = async (jobData) => {
    try {
      await apiService.createFacebookJob(jobData);
      toast({
        title: 'Facebook Job Submitted',
        description: 'Your scraping job has been submitted and is now processing.',
        variant: 'default',
      });
      fetchJobs();
      setActiveView('dashboard');
    } catch (error) {
      toast({
        title: 'Job Submission Failed',
        description: error.message || 'Failed to submit Facebook scraping job',
        variant: 'destructive',
      });
    }
  };

  const submitGoogleJob = async (jobData) => {
    try {
      await apiService.createGoogleJob(jobData);
      toast({
        title: 'Google Job Submitted',
        description: 'Your scraping job has been submitted and is now processing.',
        variant: 'default',
      });
      fetchJobs();
      setActiveView('dashboard');
    } catch (error) {
      toast({
        title: 'Job Submission Failed',
        description: error.message || 'Failed to submit Google scraping job',
        variant: 'destructive',
      });
    }
  };

  const submitYellowPagesJob = async (jobData) => {
    try {
      await apiService.createYellowPagesJob(jobData);
      toast({
        title: 'YellowPages Job Submitted',
        description: 'Your scraping job has been submitted and is now processing.',
        variant: 'default',
      });
      fetchJobs();
      setActiveView('dashboard');
    } catch (error) {
      toast({
        title: 'Job Submission Failed',
        description: error.message || 'Failed to submit YellowPages scraping job',
        variant: 'destructive',
      });
    }
  };

  const submitZillowJob = async (jobData) => {
    try {
      await apiService.createZillowJob(jobData);
      toast({
        title: 'Zillow Job Submitted',
        description: 'Your scraping job has been submitted and is now processing.',
        variant: 'default',
      });
      fetchJobs();
      setActiveView('dashboard');
    } catch (error) {
      toast({
        title: 'Job Submission Failed',
        description: error.message || 'Failed to submit Zillow scraping job',
        variant: 'destructive',
      });
    }
  };

  const submitTruliaJob = async (jobData) => {
    try {
      await apiService.createTruliaJob(jobData);
      toast({
        title: 'Trulia Job Submitted',
        description: 'Your scraping job has been submitted and is pending the Trulia extension.',
        variant: 'default',
      });
      fetchJobs();
      setActiveView('dashboard');
    } catch (error) {
      toast({
        title: 'Job Submission Failed',
        description: error.message || 'Failed to submit Trulia scraping job',
        variant: 'destructive',
      });
    }
  };

  const submitCraigslistJob = async (jobData) => {
    try {
      await apiService.createCraigslistJob(jobData);
      toast({
        title: 'Craigslist Job Submitted',
        description: 'Your scraping job has been submitted and is now processing.',
        variant: 'default',
      });
      fetchJobs();
      setActiveView('dashboard');
    } catch (error) {
      toast({
        title: 'Job Submission Failed',
        description: error.message || 'Failed to submit Craigslist scraping job',
        variant: 'destructive',
      });
    }
  };

  const viewJobResults = useCallback(
    async (job) => {
      const result = await getJobResults(job.id);
      if (result.success) {
        modalOpenRef.current = true;
        setResultsDialog({ open: true, job, results: result.results });
      } else {
        alert(result.error);
      }
    },
    [getJobResults]
  );

  const getFilteredResults = (results, scraperType = 'unknown') => {
    if (!results || results.length === 0) return [];
    return results.map((result) => {
      const displayRow = {};
      if (scraperType === 'rink') {
        displayRow['First Name'] =
          result.name ||
          result.Name ||
          result.fname ||
          result.fName ||
          result.first_name ||
          result.firstName ||
          result.First_Name ||
          result.FirstName ||
          '';
        displayRow['Last Name'] =
          result.lname ||
          result.lName ||
          result.last_name ||
          result.lastName ||
          result.Last_Name ||
          result.LastName ||
          '';
        displayRow['Address'] = result.address || result.Address || '';
        displayRow['Phone Number'] =
          result.phone || result.Phone || result.phone_number || result.phoneNumber || '';
        displayRow['City'] = result.city || result.City || '';
        displayRow['State'] =
          result.state || result.State || result.state_province || result.stateProvince || '';
        displayRow['Zip Code'] =
          result.zip ||
          result.Zip ||
          result.zipcode ||
          result.Zipcode ||
          result.postal_code ||
          result.postalCode ||
          '';
      } else if (scraperType === 'trulia') {
        displayRow['Listing ID'] =
          result.listing_id || result.listingId || result.property_id || '';
        displayRow['Address'] = result.address || result.Address || '';
        displayRow['Search State'] = result.search_state || result.searchState || '';
      } else if (scraperType === 'yellow_pages') {
        displayRow['Name'] = result.business_name || result.name || result.Name || '';
        displayRow['phone'] = result.business_phone || result.phone || result.Phone || '';
        displayRow['website'] = result.business_website || result.website || result.Website || '';
        displayRow['email'] = result.email || result.Email || '';
        displayRow['address'] = result.business_address || result.address || result.Address || '';
        displayRow['description'] = result.description || result.Description || '';
        displayRow['search_te'] = result.search_term || result.search_te || result.Search_Te || '';
        displayRow['search_location'] = result.search_location || result.Search_Location || '';
      } else {
        displayRow['Address'] =
          result.address ||
          result.Address ||
          result.business_address ||
          result.businessAddress ||
          '';
        displayRow['Price'] =
          result.price || result.Price || result.property_value || result.propertyValue || '';
        displayRow['Phone'] =
          result.phone ||
          result.Phone ||
          result.phone_number ||
          result.phoneNumber ||
          result.business_phone ||
          result.businessPhone ||
          '';
        displayRow['City'] = result.city || result.City || '';
        displayRow['State'] = result.state || result.State || '';
        displayRow['Zipcode'] =
          result.zipcode || result.Zipcode || result.zip || result.Zip || '';
        displayRow['Bedrooms'] =
          result.bedrooms || result.Bedrooms || result.bedroom || result.Bedroom || '';
        displayRow['Bathrooms'] =
          result.bathrooms || result.Bathrooms || result.bathroom || result.Bathroom || '';
        displayRow['Lot sqft'] =
          result.lot_sqft ||
          result.lotSqft ||
          result.sqft ||
          result.Sqft ||
          result.area ||
          result.Area ||
          '';
        displayRow['Property URL'] =
          result.url ||
          result.URL ||
          result.property_url ||
          result.propertyUrl ||
          result.business_website ||
          result.businessWebsite ||
          result.website ||
          result.Website ||
          '';
        displayRow['Home Type Description'] =
          result.home_type ||
          result.Home_Type ||
          result.homeType ||
          result.description ||
          result.Description ||
          result.property_type ||
          result.propertyType ||
          result.business_name ||
          result.businessName ||
          result.name ||
          result.Name ||
          '';
      }
      return displayRow;
    });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert('New passwords do not match');
      return;
    }
    const result = await changePassword({
      current_password: passwordForm.current_password,
      new_password: passwordForm.new_password,
    });
    if (result.success) {
      alert('Password changed successfully');
      setPasswordDialog({ open: false });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } else {
      alert(result.error);
    }
  };

  const handleCancelSubscription = async () => {
    if (confirm('Are you sure you want to cancel your subscription?')) {
      const result = await cancelSubscription();
      if (result.success) {
        alert('Subscription cancelled successfully');
        setSubscriptionDialog({ open: false });
      } else {
        alert(result.error);
      }
    }
  };

  const handlePasswordFormChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  if (authLoading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;

  const dashboardShellProps = {
    user,
    scrapers,
    activeView,
    setActiveView,
    activeScraper,
    setActiveScraper,
    sidebarCollapsed,
    setSidebarCollapsed,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    jobs,
    dashboardStats,
    pagination,
    jobsLoading,
    searchTerm,
    setSearchTerm,
    handlePageChange,
    handleSearch,
    viewJobResults,
    deleteJob,
    getJobResults,
    downloadResults,
    getFilteredResults,
    showDeleteConfirmation,
    toast,
    fetchJobs,
    locations,
    submitRinkJob,
    submitFacebookJob,
    submitGoogleJob,
    submitYellowPagesJob,
    submitZillowJob,
    submitTruliaJob,
    submitCraigslistJob,
    profileDialog,
    setProfileDialog,
    passwordDialog,
    setPasswordDialog,
    subscriptionDialog,
    setSubscriptionDialog,
    paymentMethodDialog,
    setPaymentMethodDialog,
    passwordForm,
    handleLogout,
    handlePasswordChange,
    handlePasswordFormChange,
    handleCancelSubscription,
    resultsDialog,
    setResultsDialog,
    confirmDialog,
    closeConfirmDialog,
    handleConfirmDelete,
    modalOpenRef,
  };

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <DashboardShell {...dashboardShellProps} />
    </Suspense>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AnalyticsBootstrap />
        <SessionExpiredBridge />
        <ReferralCodeHandler />
        <PublicAppStoreQRPopup />
        <Routes>
          <Route path="/" element={<Website />}>
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="reviews" element={<ReviewsPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="web-scraping" element={<WebScrapingPage />} />
            <Route path="web-integrations" element={<WebIntegrationsPage />} />
            <Route path="scrapers/:slug" element={<ScraperLandingPage />} />
          </Route>
          <Route path="/privacy" element={<PrivacyRoute />} />
          <Route path="/terms" element={<TermsRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/reset-password" element={<ResetPasswordRoute />} />
          <Route path="/dashboard" element={<ProtectedApp />} />
          <Route path="/admin" element={<ProtectedApp />} />
          <Route
            path="*"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <NotFoundPage />
              </Suspense>
            }
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
